import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import InventoryManager from './InventoryManager';
import './InventoryManagerComponent.css';

/**
 * Item types with their icons
 */
const ITEM_TYPES = {
  WEAPON: '⚔️',
  ARMOR: '🛡️',
  POTION: '🧪',
  SCROLL: '📜',
  MISCELLANEOUS: '🔮'
};

/**
 * Item rarity color classes
 */
const RARITY_COLORS = {
  COMMON: 'rarity-common',
  UNCOMMON: 'rarity-uncommon',
  RARE: 'rarity-rare',
  VERY_RARE: 'rarity-very-rare',
  LEGENDARY: 'rarity-legendary'
};

/**
 * Inventory Management Component
 */
const InventoryManagerComponent = ({ 
  character, 
  onCharacterUpdate,
  onItemUse,
  readOnly = false
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [filteredItems, setFilteredItems] = useState([]);
  const [inventoryManager] = useState(() => new InventoryManager());
  const [showItemDetails, setShowItemDetails] = useState(false);
  const [encumbrance, setEncumbrance] = useState({
    current: 0,
    maximum: 0,
    level: 'normal' // normal, encumbered, heavily-encumbered
  });
  
  // Filter items based on active tab
  useEffect(() => {
    if (!character?.items) {
      setFilteredItems([]);
      return;
    }
    
    if (activeTab === 'all') {
      setFilteredItems(character.items);
    } else {
      setFilteredItems(
        character.items.filter(item => item.item.type === activeTab)
      );
    }
  }, [character?.items, activeTab]);
  
  // Calculate encumbrance
  useEffect(() => {
    if (!character) return;
    
    const calculateItemWeight = (item) => {
      return (item.weight || 0) * item.quantity;
    };
    
    const totalWeight = character.items?.reduce(
      (sum, item) => sum + calculateItemWeight(item.item), 
      0
    ) || 0;
    
    const maxWeight = character.carryingCapacity || (character.strength * 15);
    
    let level = 'normal';
    if (totalWeight > maxWeight * 0.5) {
      level = 'encumbered';
    }
    if (totalWeight > maxWeight * 0.8) {
      level = 'heavily-encumbered';
    }
    
    setEncumbrance({
      current: totalWeight,
      maximum: maxWeight,
      level
    });
  }, [character]);
  
  const handleUseItem = (item) => {
    if (readOnly) return;
    
    try {
      const result = inventoryManager.useItem(
        { character }, 
        { itemId: item.item.id }
      );
      
      if (result.success) {
        if (onCharacterUpdate) {
          onCharacterUpdate(result.updatedCharacter);
        }
        
        if (onItemUse) {
          onItemUse(result);
        }
      }
    } catch (error) {
      console.error('Error using item:', error);
    }
  };
  
  const handleEquipItem = (item) => {
    if (readOnly) return;
    
    try {
      const result = inventoryManager.equipItem(
        { updatedCharacter: { ...character } },
        item.item,
        character.items.findIndex(i => i.item.id === item.item.id)
      );
      
      if (onCharacterUpdate) {
        onCharacterUpdate(result.updatedCharacter);
      }
    } catch (error) {
      console.error('Error equipping item:', error);
    }
  };
  
  const handleDropItem = (item) => {
    if (readOnly) return;
    
    if (window.confirm(`Are you sure you want to drop ${item.item.name}?`)) {
      try {
        const updatedCharacter = inventoryManager.removeItem(
          { ...character },
          item.item.id,
          1
        );
        
        if (onCharacterUpdate) {
          onCharacterUpdate(updatedCharacter);
        }
      } catch (error) {
        console.error('Error dropping item:', error);
      }
    }
  };
  
  // Determine if an item is usable
  const isItemUsable = (item) => {
    return ['POTION', 'SCROLL'].includes(item.item.type);
  };
  
  // Determine if an item is equippable
  const isItemEquippable = (item) => {
    return ['WEAPON', 'ARMOR'].includes(item.item.type);
  };
  
  // Format item weight display
  const formatWeight = (weight) => {
    if (!weight) return '0 lb';
    return `${weight} lb`;
  };
  
  // Render inventory tab navigation
  const renderTabNavigation = () => (
    <div className="inventory-tabs">
      <button 
        className={`tab ${activeTab === 'all' ? 'active' : ''}`}
        onClick={() => setActiveTab('all')}
      >
        All
      </button>
      <button 
        className={`tab ${activeTab === 'WEAPON' ? 'active' : ''}`}
        onClick={() => setActiveTab('WEAPON')}
      >
        {ITEM_TYPES.WEAPON} Weapons
      </button>
      <button 
        className={`tab ${activeTab === 'ARMOR' ? 'active' : ''}`}
        onClick={() => setActiveTab('ARMOR')}
      >
        {ITEM_TYPES.ARMOR} Armor
      </button>
      <button 
        className={`tab ${activeTab === 'POTION' ? 'active' : ''}`}
        onClick={() => setActiveTab('POTION')}
      >
        {ITEM_TYPES.POTION} Potions
      </button>
      <button 
        className={`tab ${activeTab === 'SCROLL' ? 'active' : ''}`}
        onClick={() => setActiveTab('SCROLL')}
      >
        {ITEM_TYPES.SCROLL} Scrolls
      </button>
      <button 
        className={`tab ${activeTab === 'MISCELLANEOUS' ? 'active' : ''}`}
        onClick={() => setActiveTab('MISCELLANEOUS')}
      >
        {ITEM_TYPES.MISCELLANEOUS} Misc
      </button>
    </div>
  );
  
  // Render list of inventory items
  const renderInventoryItems = () => {
    if (!filteredItems.length) {
      return (
        <div className="empty-inventory">
          <p>No items in this category</p>
        </div>
      );
    }
    
    return (
      <ScrollArea className="inventory-items">
        {filteredItems.map((item, index) => (
          <div 
            key={`${item.item.id}-${index}`}
            className={`inventory-item ${selectedItem?.item.id === item.item.id ? 'selected' : ''} ${item.isEquipped ? 'equipped' : ''}`}
            onClick={() => {
              setSelectedItem(item);
              setShowItemDetails(true);
            }}
          >
            <div className="item-icon">
              {ITEM_TYPES[item.item.type] || '📦'}
            </div>
            
            <div className={`item-details ${RARITY_COLORS[item.item.rarity] || ''}`}>
              <div className="item-name">
                {item.isEquipped && <span className="equipped-indicator">⚡</span>}
                {item.item.name}
              </div>
              <div className="item-info">
                <span>{item.item.type}</span>
                {item.quantity > 1 && <span className="item-quantity">x{item.quantity}</span>}
                <span className="item-weight">{formatWeight(item.item.weight)}</span>
              </div>
            </div>
          </div>
        ))}
      </ScrollArea>
    );
  };
  
  // Render detailed view of selected item
  const renderItemDetails = () => {
    if (!selectedItem) return null;
    
    const item = selectedItem.item;
    
    return (
      <Card className="item-details-card">
        <CardHeader>
          <CardTitle className={RARITY_COLORS[item.rarity] || ''}>
            {ITEM_TYPES[item.type] || '📦'} {item.name}
          </CardTitle>
          <CardDescription>
            {item.type} • {item.rarity.replace('_', ' ')}
            {item.weight && ` • ${formatWeight(item.weight)}`}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <p className="item-description">{item.description}</p>
          
          {item.stats && (
            <div className="item-stats">
              {Object.entries(item.stats).map(([stat, value]) => (
                <div key={stat} className="stat-row">
                  <span className="stat-name">{stat}:</span>
                  <span className="stat-value">{value}</span>
                </div>
              ))}
            </div>
          )}
          
          <div className="item-value">
            Value: {item.value} gold
          </div>
        </CardContent>
        
        <CardFooter className="item-actions">
          {!readOnly && (
            <>
              {isItemUsable(selectedItem) && (
                <Button onClick={() => handleUseItem(selectedItem)}>
                  Use
                </Button>
              )}
              
              {isItemEquippable(selectedItem) && (
                <Button 
                  variant={selectedItem.isEquipped ? "secondary" : "default"}
                  onClick={() => handleEquipItem(selectedItem)}
                >
                  {selectedItem.isEquipped ? 'Unequip' : 'Equip'}
                </Button>
              )}
              
              <Button 
                variant="destructive"
                onClick={() => handleDropItem(selectedItem)}
              >
                Drop
              </Button>
            </>
          )}
          
          <Button 
            variant="outline"
            onClick={() => {
              setSelectedItem(null);
              setShowItemDetails(false);
            }}
          >
            Close
          </Button>
        </CardFooter>
      </Card>
    );
  };
  
  // Render encumbrance indicator
  const renderEncumbrance = () => {
    if (!character) return null;
    
    const percentFull = Math.min(100, (encumbrance.current / encumbrance.maximum) * 100);
    
    return (
      <div className="encumbrance-indicator">
        <div className="encumbrance-label">
          Carrying Capacity: {encumbrance.current}/{encumbrance.maximum} lb
        </div>
        <div className="encumbrance-bar">
          <div 
            className={`encumbrance-fill encumbrance-${encumbrance.level}`}
            style={{ width: `${percentFull}%` }}
          />
        </div>
      </div>
    );
  };
  
  return (
    <div className="inventory-manager">
      <Card>
        <CardHeader>
          <CardTitle>Inventory</CardTitle>
          <CardDescription>
            {character?.items?.length || 0} items in inventory
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {renderEncumbrance()}
          {renderTabNavigation()}
          {renderInventoryItems()}
        </CardContent>
      </Card>
      
      {showItemDetails && renderItemDetails()}
    </div>
  );
};

export default InventoryManagerComponent;